// Словари и расчёты раздела «Овертаймы и командировки». Без React — чтобы их можно было
// проверить отдельно от отрисовки (tests/p73x.test.tsx).
//
// Здесь же собраны оговорки, которые экран ОБЯЗАН показать: данные бытового блока читаются
// неверно без них (например, «19 командировок» — на самом деле 8 поездок, 11 выдач денег
// под отчёт и три пробы).

export const РАЗМЕР_СТРАНИЦЫ = 50

/** Состояние оплаты записи овертайма. */
export function видОплаты(pay: string | null | undefined): {
  ярлык: string
  смысл: string
  класс: string
} {
  switch (pay) {
    case 'paid':
      return { ярлык: 'оплачен', смысл: 'вошёл в оплаченную ведомость, правка закрыта',
               класс: 'badge won' }
    case 'in_batch':
      return { ярлык: 'в ведомости', смысл: 'включён в черновик ведомости, ждёт оплаты',
               класс: 'badge work' }
    case 'free':
      return { ярлык: 'не в ведомости', смысл: 'ещё не включён ни в одну ведомость',
               класс: 'badge' }
    default:
      // Незнакомый статус показываем как есть: портал волен завести новый, и «—» на его
      // месте читалось бы как «статуса нет».
      return { ярлык: pay || 'статус не указан', смысл: 'статус портала неизвестен CRM',
               класс: 'badge' }
  }
}

/** Согласование. Отдельно от оплаты: оплатить могут и несогласованное. */
export function видСогласования(approved: boolean | null | undefined): {
  ярлык: string
  класс: string
} {
  if (approved === true) return { ярлык: 'согласован', класс: 'badge won' }
  if (approved === false) return { ярлык: 'ждёт согласования', класс: 'badge work' }
  return { ярлык: 'неизвестно', класс: 'badge' }
}

/** Вид записи о командировке. Два разных процесса в одной таблице. */
export function видПоездки(trip_kind: string | null | undefined): {
  ярлык: string
  смысл: string
  поездка: boolean
} {
  if (trip_kind === 'ds') {
    return { ярлык: 'деньги под отчёт', поездка: false,
             смысл: 'выдача денег без поездки — суточных здесь нет' }
  }
  if (trip_kind === 'engineer') {
    return { ярлык: 'командировка', поездка: true, смысл: 'поездка с суточными' }
  }
  return { ярлык: trip_kind || 'вид не указан', поездка: false,
           смысл: 'вид не указан — считать это поездкой нельзя' }
}

/** Ставка суточных зависит от того, РФ это или заграница. */
export function видСтраны(kind: string | null | undefined): string {
  if (kind === 'foreign') return 'заграница'
  if (kind === 'ru') return 'РФ'
  return kind || '—'
}

const ПРОБНЫЕ = ['тест', 'test', 'проба']

/**
 * Запись похожа на пробную (в данных их три: «тест», «Тест», «тестовый город»).
 * Не прячем такие строки, а помечаем: спрятать — значит однажды спрятать настоящую.
 */
export function похожеНаПробу(...поля: (string | null | undefined)[]): boolean {
  const т = поля.filter(Boolean).join(' ').toLowerCase()
  return ПРОБНЫЕ.some((с) => т.includes(с))
}

/** Часы одной записи: работа + дорога. Дежурства и рейды считаются не в часах. */
export function часы(r: { work_hours: number | null; road_hours: number | null }): number {
  return (r.work_hours ?? 0) + (r.road_hours ?? 0)
}

/** Поездка ещё не закрыта. */
export function вПути(r: { status: string | null }): boolean {
  return r.status === 'open'
}

/**
 * Командировка «открыта»: срок ещё не кончился либо она не отправлена в бухгалтерию.
 * Считаем по тем же правилам, что ядро в ?open=1 — иначе счётчик на экране разойдётся
 * со списком, который тот же экран показывает.
 */
export function командировкаОткрыта(
  r: { date_to: string | null; status: string | null },
  сегодня: string,
): boolean {
  if (r.status === 'draft' || r.status === 'active') return true
  return !!r.date_to && r.date_to >= сегодня
}

/** Сумма расходов авансового отчёта (перелёты, такси, покупки). */
export function расходы(r: { expenses: { amount?: number }[] | null }): number {
  return (r.expenses ?? []).reduce((s, e) => s + (Number(e.amount) || 0), 0)
}

/** Часы и минуты из дробного числа часов: «7.5» → «7 ч 30 мин». */
export function часыСловами(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—'
  const целых = Math.floor(v)
  const минут = Math.round((v - целых) * 60)
  if (!минут) return `${целых} ч`
  return `${целых} ч ${минут} мин`
}

/**
 * Насколько данные устарели. Возвращает худшую по разделу сущность: экран показывает
 * именно её, потому что человек смотрит на раздел целиком, а не на восемь таблиц.
 */
export function свежесть(
  список: { entity: string; age_min: number | null }[] | undefined,
): { entity: string; age_min: number } | null {
  const с = (список ?? []).filter((x) => x.age_min !== null) as {
    entity: string
    age_min: number
  }[]
  if (!с.length) return null
  return с.reduce((a, b) => (b.age_min > a.age_min ? b : a))
}

/** Короткое имя сущности синка для показа человеку. */
export const ИМЯ_СУЩНОСТИ: Record<string, string> = {
  'p73x:overtime': 'овертаймы',
  'p73x:overtime_batches': 'ведомости',
  'p73x:overtime_errors': 'сообщения об ошибках',
  'p73x:travel_reports': 'командировки',
  'p73x:cars': 'машины',
  'p73x:car_trips': 'поездки',
  'p73x:car_refuels': 'заправки',
  'p73x:taxi_trips': 'такси',
}

/**
 * Оговорки, без которых числа раздела читаются неверно. Тексты приходят из ядра
 * (refs.data_notes) — здесь только порядок и то, какие показывать всегда.
 */
export const ВАЖНЫЕ_ОГОВОРКИ = ['overtime_depth', 'trip_kinds', 'trip_tests', 'car_trips_lag']

/** Месяц «ГГГГ-ММ» словами: «2026-07» → «июль 2026». */
const МЕСЯЦЫ = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август',
  'сентябрь', 'октябрь', 'ноябрь', 'декабрь']

export function месяцСловами(m: string | null | undefined): string {
  const s = String(m ?? '')
  const п = /^(\d{4})-(\d{2})$/.exec(s)
  if (!п) return s || '—'
  const i = Number(п[2]) - 1
  return `${МЕСЯЦЫ[i] ?? п[2]} ${п[1]}`
}

/** Текущий месяц в виде «ГГГГ-ММ» по локальной дате. */
export function текущийМесяц(сегодня: Date = new Date()): string {
  const m = String(сегодня.getMonth() + 1).padStart(2, '0')
  return `${сегодня.getFullYear()}-${m}`
}
